import React, { useState, useRef, useMemo, useLayoutEffect } from 'react';
import { useFrame } from '../utils/useFrameP1'; // P1: igual ao do r3f; com ?dbg=1 mede o tempo dos handlers
import { sameSliceContent } from '../utils/sliceContent';
import SceneContent from './3d/SceneContent';
import { syncCount, syncLoteDisparo, SC } from '../utils/syncProbe';
import { newestAbsOf, positionFor, type AnchoredNav } from '../utils/anchoredNav';

const VISIBLE_COUNT = 1000;
const OVERSCAN = 500;

export const ChartDataSlicer = (props: any) => {
  const { processedData, historyOffsetRef, seriesId, ...rest } = props;
  syncCount(SC.SLICER_RENDER); // diagnóstico (?dbg=1)
  
  // F2: o lote é guardado como CHAVE estável (nav.keyOf): com a política 'acompanhaPresente' é a distância do fim de
  // sempre; com 'mesmasVelas' é o índice global da vela, e não muda quando chega vela nova. `sliceOffset` (distância do
  // fim relativa a ESTA série) é o que o resto da cena recebe. A posição também é lida relativa a esta série.
  const nav: AnchoredNav | undefined = historyOffsetRef?.nav;
  const dataNewest = newestAbsOf(processedData);
  const [sliceKey, setSliceKey] = useState(() => (nav ? nav.keyOf(0, dataNewest) : 0));
  // F3b C2: série nova (troca de timeframe/ativo no mesmo momento): a chave guardada é da série anterior. O lote nasce
  // na posição atual (já posta no momento da troca) neste mesmo render, sem esperar o useFrame; a chave é acertada no
  // efeito de layout (antes do quadro). Sem troca de série, nada muda.
  const sliceSeriesRef = useRef(seriesId);
  const freshSeries = !Object.is(sliceSeriesRef.current, seriesId);
  const sliceOffset = freshSeries
    ? Math.max(0, Math.floor(positionFor(historyOffsetRef, dataNewest)))
    : (nav ? nav.distOf(sliceKey, dataNewest) : sliceKey);
  useLayoutEffect(() => {
    if (!freshSeries) return;
    sliceSeriesRef.current = seriesId;
    const k = nav ? nav.keyOf(sliceOffset, dataNewest) : sliceOffset;
    if (k !== sliceKey) setSliceKey(k);
  });
  const setSliceOffset = (dist: number) => setSliceKey(nav ? nav.keyOf(dist, dataNewest) : dist);
  const prevIntOffset = useRef(0);
  const lastUpdateRef = useRef(0);

  useFrame(() => {
    if (!historyOffsetRef) return;
    const pos = positionFor(historyOffsetRef, dataNewest);
    const currentInt = Math.max(0, Math.floor(pos));
    const now = performance.now();
    
    // Fast path: if at the present (0) and sliceOffset is not 0, immediately snap to 0
    if (currentInt === 0 && sliceOffset !== 0) {
      prevIntOffset.current = 0;
      setSliceOffset(0);
      syncLoteDisparo();
      lastUpdateRef.current = now;
    } else if (Math.abs(currentInt - sliceOffset) >= 100) {
      // Distance Chunking & Throttle: Trigger React re-renders when offset moves by >= 100 candles,
      // but throttle to a maximum of 12.5Hz (every 80ms) to prevent React state thrashing during high-speed autoplay.
      if (now - lastUpdateRef.current > 80) {
        prevIntOffset.current = currentInt;
        setSliceOffset(currentInt);
        syncLoteDisparo();
        lastUpdateRef.current = now;
      }
    } else if (currentInt === Math.floor(pos) && sliceOffset !== currentInt) {
      // Catch-up when completely settled (movement stopped)
      const isMoving = Math.abs(pos - ((historyOffsetRef as any).lastFrameValue || 0)) > 0.001;
      if (!isMoving) {
        prevIntOffset.current = currentInt;
        setSliceOffset(currentInt);
        syncLoteDisparo();
        lastUpdateRef.current = now;
      }
    }
    (historyOffsetRef as any).lastFrameValue = pos;
  });

  // C3: a fatia só vira referência NOVA quando o conteúdo mudou. Num tick, o app recria todos os objetos de vela e
  // devolve um array novo, mas no fundo do histórico os pontos da janela têm os mesmos valores: sem isso a cena
  // reconstruía a geometria e enviava buffers à GPU a cada tick sem mudar nada visível.
  const prevSliceRef = useRef<{ slicedData: any[]; padLeft: number; padRight: number; effectiveVisible: number } | null>(null);
  const { slicedData, padLeft, padRight, effectiveVisible } = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return { slicedData: [], padLeft: 0, padRight: 0, effectiveVisible: VISIBLE_COUNT };
    }
    const totalLen = processedData.length;
    const effVis = Math.min(VISIBLE_COUNT, totalLen);
    const visibleEnd = Math.max(effVis, totalLen - sliceOffset);
    const visibleStart = Math.max(0, visibleEnd - effVis);

    const actualStart = Math.max(0, visibleStart - OVERSCAN);
    const actualEnd = Math.min(totalLen, visibleEnd + OVERSCAN);

    const pLeft = visibleStart - actualStart;
    const pRight = actualEnd - visibleEnd;

    const next = processedData.slice(actualStart, actualEnd);
    const prev = prevSliceRef.current;
    if (prev && prev.padLeft === pLeft && prev.padRight === pRight && prev.effectiveVisible === effVis && sameSliceContent(prev.slicedData, next)) {
      return prev;
    }
    return {
      slicedData: next,
      padLeft: pLeft,
      padRight: pRight,
      effectiveVisible: effVis
    };
  }, [processedData, sliceOffset]);
  prevSliceRef.current = { slicedData, padLeft, padRight, effectiveVisible };

  return (
    <SceneContent 
      data={slicedData} 
      historyOffsetRef={historyOffsetRef} 
      sliceOffset={sliceOffset} 
      dataNewestAbs={dataNewest}
      seriesId={seriesId}
      padLeft={padLeft}
      padRight={padRight}
      visibleCount={effectiveVisible}
      {...rest} 
    />
  );
};
